import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../../lib/utils'

/**
 * Universal color picker — a draggable saturation/lightness canvas, a hue
 * slider, an alpha slider, and synced HEX + R/G/B fields. Adapted from a
 * standalone reference into the app's design tokens (so it themes with the rest
 * of the UI, dark mode included) and made fully controlled.
 *
 * Canonical value is any CSS color the rest of the app already stores — `#hex`,
 * `#rrggbbaa`, `rgb()`, or `rgba()`. `onChange` emits `#RRGGBB` when fully
 * opaque and `rgba(r, g, b, a)` once the alpha slider drops below 1, so existing
 * translucent values (e.g. card backgrounds) round-trip without losing their
 * transparency.
 *
 * `ColorPickerPanel` is the bare UI; `ColorPickerPopover` floats it next to an
 * anchor element via a portal (used by the shared ColorInput swatch).
 */

// ── Color math ────────────────────────────────────────────────────────────
export type RGBA = { r: number; g: number; b: number; a: number }
type HSL = { h: number; s: number; l: number }

export function parseColor(input?: string | null): RGBA {
  if (!input) return { r: 0, g: 0, b: 0, a: 1 }
  const c = String(input).trim().toLowerCase()
  if (c === 'transparent') return { r: 0, g: 0, b: 0, a: 0 }

  if (c.startsWith('#')) {
    let h = c.slice(1)
    if (h.length === 3) h = h.split('').map(x => x + x).join('')
    if (h.length === 4) h = h.split('').map(x => x + x).join('') // #rgba → #rrggbbaa
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16)
      const g = parseInt(h.slice(2, 4), 16)
      const b = parseInt(h.slice(4, 6), 16)
      const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
      if ([r, g, b].every(n => !Number.isNaN(n))) return { r, g, b, a }
    }
    return { r: 0, g: 0, b: 0, a: 1 }
  }

  const m = c.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)/)
  if (m) {
    return {
      r: Math.round(+m[1]), g: Math.round(+m[2]), b: Math.round(+m[3]),
      a: m[4] !== undefined ? Math.max(0, Math.min(1, parseFloat(m[4]))) : 1,
    }
  }
  return { r: 0, g: 0, b: 0, a: 1 }
}

export function rgbToHsl(r: number, g: number, b: number): HSL {
  r /= 255; g /= 255; b /= 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b)
  let h = 0, s = 0
  const l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break
      case g: h = (b - r) / d + 2; break
      default: h = (r - g) / d + 4; break
    }
    h *= 60
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) }
}

export function hslToRgb(h: number, s: number, l: number): { r: number; g: number; b: number } {
  s /= 100; l /= 100
  const k = (n: number) => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = (n: number) => l - a * Math.max(Math.min(k(n) - 3, 9 - k(n), 1), -1)
  return {
    r: Math.round(255 * f(0)),
    g: Math.round(255 * f(8)),
    b: Math.round(255 * f(4)),
  }
}

const toHex2 = (x: number) => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')
export function rgbToHex(r: number, g: number, b: number): string {
  return `#${toHex2(r)}${toHex2(g)}${toHex2(b)}`.toUpperCase()
}

/** Emit hex when opaque, rgba() otherwise — matches how the app stores colors. */
export function formatColor(r: number, g: number, b: number, a: number): string {
  if (a >= 1) return rgbToHex(r, g, b)
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${Math.round(a * 100) / 100})`
}

// ── Saturation / lightness canvas ───────────────────────────────────────────
function DraggableColorCanvas({
  h, s, l, onChange,
}: HSL & { onChange: (next: { s: number; l: number }) => void }) {
  const [dragging, setDragging] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  const calc = useCallback((clientX: number, clientY: number) => {
    const el = ref.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width))
    const y = Math.max(0, Math.min(clientY - rect.top, rect.height))
    onChange({ s: Math.round((x / rect.width) * 100), l: 100 - Math.round((y / rect.height) * 100) })
  }, [onChange])

  useEffect(() => {
    if (!dragging) return
    const move = (e: MouseEvent) => { e.preventDefault(); calc(e.clientX, e.clientY) }
    const touch = (e: TouchEvent) => { const t = e.touches[0]; if (t) { e.preventDefault(); calc(t.clientX, t.clientY) } }
    const up = () => setDragging(false)
    window.addEventListener('mousemove', move)
    window.addEventListener('mouseup', up)
    window.addEventListener('touchmove', touch, { passive: false })
    window.addEventListener('touchend', up)
    return () => {
      window.removeEventListener('mousemove', move)
      window.removeEventListener('mouseup', up)
      window.removeEventListener('touchmove', touch)
      window.removeEventListener('touchend', up)
    }
  }, [dragging, calc])

  return (
    <div
      ref={ref}
      className="relative h-40 w-full touch-none overflow-hidden rounded-lg border border-border"
      style={{
        cursor: 'crosshair',
        background: `linear-gradient(to top, #000, transparent, #fff), linear-gradient(to left, hsl(${h}, 100%, 50%), #bbb)`,
      }}
      onMouseDown={e => { e.preventDefault(); setDragging(true); calc(e.clientX, e.clientY) }}
      onTouchStart={e => { const t = e.touches[0]; if (t) { setDragging(true); calc(t.clientX, t.clientY) } }}
    >
      <div
        className="pointer-events-none absolute h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-4 border-white shadow ring-1 ring-black/20"
        style={{ left: `${s}%`, top: `${100 - l}%`, background: `hsl(${h}, ${s}%, ${l}%)` }}
      />
    </div>
  )
}

// Range thumb styling (shared by hue + alpha sliders). Injected once.
const SLIDER_STYLE_ID = 'meno-colorpicker-slider-style'
const SLIDER_CSS = `
.meno-color-slider{-webkit-appearance:none;appearance:none}
.meno-color-slider::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;width:18px;height:18px;border-radius:50%;background:transparent;border:4px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.25);cursor:pointer}
.meno-color-slider::-moz-range-thumb{width:18px;height:18px;border-radius:50%;background:transparent;border:4px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,0.25);cursor:pointer}
`
function useSliderStyle() {
  useEffect(() => {
    if (document.getElementById(SLIDER_STYLE_ID)) return
    const el = document.createElement('style')
    el.id = SLIDER_STYLE_ID
    el.textContent = SLIDER_CSS
    document.head.appendChild(el)
  }, [])
}

// ── Panel ───────────────────────────────────────────────────────────────────
export function ColorPickerPanel({
  value, onChange, className,
}: {
  value: string
  onChange: (v: string) => void
  className?: string
}) {
  useSliderStyle()
  const [hsl, setHsl] = useState<HSL>(() => { const { r, g, b } = parseColor(value); return rgbToHsl(r, g, b) })
  const [alpha, setAlpha] = useState<number>(() => parseColor(value).a)
  const [hexDraft, setHexDraft] = useState<string>(() => { const { r, g, b } = parseColor(value); return rgbToHex(r, g, b).slice(1) })
  const lastEmit = useRef<string>('')

  // Sync internal state from the controlled value, but skip the echo of our own
  // emit so dragging doesn't fight the round-trip. Preserve hue when the color
  // is greyscale (s≈0) so the hue slider doesn't jump to 0.
  useEffect(() => {
    if (value === lastEmit.current) return
    const { r, g, b, a } = parseColor(value)
    const next = rgbToHsl(r, g, b)
    setHsl(prev => ({ h: next.s === 0 ? prev.h : next.h, s: next.s, l: next.l }))
    setAlpha(a)
    setHexDraft(rgbToHex(r, g, b).slice(1))
  }, [value])

  const rgb = hslToRgb(hsl.h, hsl.s, hsl.l)
  const hex = rgbToHex(rgb.r, rgb.g, rgb.b)

  const emit = (next: { hsl?: HSL; alpha?: number }) => {
    const h = next.hsl ?? hsl
    const a = next.alpha ?? alpha
    const c = hslToRgb(h.h, h.s, h.l)
    const out = formatColor(c.r, c.g, c.b, a)
    lastEmit.current = out
    if (next.hsl) setHsl(next.hsl)
    if (next.alpha !== undefined) setAlpha(next.alpha)
    setHexDraft(rgbToHex(c.r, c.g, c.b).slice(1))
    onChange(out)
  }

  const onHexChange = (raw: string) => {
    const clean = raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 6).toUpperCase()
    setHexDraft(clean)
    if (clean.length === 6) {
      const { r, g, b } = parseColor('#' + clean)
      emit({ hsl: rgbToHsl(r, g, b) })
    }
  }

  const onRgbChange = (key: 'r' | 'g' | 'b', raw: string) => {
    const n = Math.max(0, Math.min(255, Math.round(Number(raw) || 0)))
    const nextRgb = { ...rgb, [key]: n }
    emit({ hsl: rgbToHsl(nextRgb.r, nextRgb.g, nextRgb.b) })
  }

  const rgbField = (key: 'r' | 'g' | 'b', label: string) => (
    <label className="flex flex-1 flex-col items-center gap-1">
      <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input
        type="number" min={0} max={255}
        value={rgb[key]}
        onChange={e => onRgbChange(key, e.target.value)}
        className="w-full rounded-md border border-border bg-background px-1 py-1 text-center text-xs text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/30"
      />
    </label>
  )

  return (
    <div className={cn('flex w-[248px] select-none flex-col gap-3', className)}>
      <DraggableColorCanvas h={hsl.h} s={hsl.s} l={hsl.l} onChange={({ s, l }) => emit({ hsl: { ...hsl, s, l } })} />

      {/* Hue */}
      <input
        type="range" min={0} max={360} value={hsl.h}
        onChange={e => emit({ hsl: { ...hsl, h: e.target.valueAsNumber } })}
        className="meno-color-slider h-3 w-full cursor-pointer rounded-full border border-border"
        style={{ background: 'linear-gradient(to right, hsl(0,100%,50%), hsl(60,100%,50%), hsl(120,100%,50%), hsl(180,100%,50%), hsl(240,100%,50%), hsl(300,100%,50%), hsl(360,100%,50%))' }}
      />

      {/* Alpha */}
      <input
        type="range" min={0} max={100} value={Math.round(alpha * 100)}
        onChange={e => emit({ alpha: e.target.valueAsNumber / 100 })}
        className="meno-color-slider h-3 w-full cursor-pointer rounded-full border border-border"
        style={{
          backgroundImage:
            `linear-gradient(to right, rgba(${rgb.r},${rgb.g},${rgb.b},0), rgb(${rgb.r},${rgb.g},${rgb.b})),` +
            'linear-gradient(45deg,#ccc 25%,transparent 25%),linear-gradient(-45deg,#ccc 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#ccc 75%),linear-gradient(-45deg,transparent 75%,#ccc 75%)',
          backgroundSize: 'auto, 8px 8px, 8px 8px, 8px 8px, 8px 8px',
          backgroundPosition: '0 0, 0 0, 0 4px, 4px -4px, -4px 0',
        }}
      />

      {/* HEX */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-sm font-medium text-muted-foreground">#</span>
          <input
            value={hexDraft}
            onChange={e => onHexChange(e.target.value)}
            spellCheck={false}
            className="w-full rounded-lg border border-border bg-background py-2 pl-5 pr-2 text-sm font-mono uppercase text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/30"
          />
        </div>
        <div
          className="h-8 w-8 shrink-0 rounded-md border border-border"
          style={{
            backgroundColor: alpha >= 1 ? hex : `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`,
          }}
        />
      </div>

      {/* RGB */}
      <div className="flex items-end gap-2">
        {rgbField('r', 'R')}
        {rgbField('g', 'G')}
        {rgbField('b', 'B')}
        <label className="flex flex-1 flex-col items-center gap-1">
          <span className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground">A%</span>
          <input
            type="number" min={0} max={100}
            value={Math.round(alpha * 100)}
            onChange={e => emit({ alpha: Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))) / 100 })}
            className="w-full rounded-md border border-border bg-background px-1 py-1 text-center text-xs text-foreground outline-none focus:border-ring focus:ring-1 focus:ring-ring/30"
          />
        </label>
      </div>
    </div>
  )
}

// ── Floating popover ────────────────────────────────────────────────────────
export function ColorPickerPopover({
  anchorEl, value, onChange, onClose,
}: {
  anchorEl: HTMLElement | null
  value: string
  onChange: (v: string) => void
  onClose: () => void
}) {
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const reposition = useCallback(() => {
    if (!anchorEl) return
    const r = anchorEl.getBoundingClientRect()
    const W = 248 + 24 // panel + padding
    const H = 360
    const gap = 6
    let left = r.right - W
    left = Math.max(8, Math.min(left, window.innerWidth - W - 8))
    let top = r.bottom + gap
    if (top + H > window.innerHeight - 8) top = Math.max(8, r.top - H - gap)
    setPos({ top, left })
  }, [anchorEl])

  useLayoutEffect(() => { reposition() }, [reposition])
  useEffect(() => {
    const handler = () => reposition()
    window.addEventListener('scroll', handler, true)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  }, [reposition])

  if (typeof document === 'undefined') return null

  return createPortal(
    <>
      <div className="fixed inset-0 z-[120]" onPointerDown={onClose} />
      <div
        ref={panelRef}
        className="fixed z-[121] rounded-2xl border border-border bg-popover p-3 shadow-xl animate-in fade-in zoom-in-95 duration-150"
        style={{ top: pos?.top ?? -9999, left: pos?.left ?? -9999, visibility: pos ? 'visible' : 'hidden' }}
        onPointerDown={e => e.stopPropagation()}
      >
        <ColorPickerPanel value={value} onChange={onChange} />
      </div>
    </>,
    document.body,
  )
}
